import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  TextField,
} from '@mui/material'
import { ErrorDialog, PowerExtensionDialog, SuccessDialog } from '../Dialogs'
import React, { useEffect, useState } from 'react'

const AbstractSelector = ({ error, id, label, options, setValue, value }) => {
  const handleSelectChange = (event, values) => {
    setValue(values)
  }

  return (
    <FormControl error={error} fullWidth={true}>
      <Autocomplete
        data-testid={`autocomplete-${label}`}
        disableCloseOnSelect={true}
        disabled={error}
        getOptionLabel={option => option.label}
        key={id}
        multiple
        onChange={handleSelectChange}
        options={options}
        renderInput={params => (
          <TextField
            {...params}
            data-testid={`text-field-${label}`}
            error={error}
            key={`text-field-${params.id}`}
            label={label}
            variant="standard"
          />
        )}
        value={value}
      />
    </FormControl>
  )
}

const PowerExtensionsLocationSelector = ({
  client,
  clientsService,
  id,
  portal,
  powerExtensionService,
}) => {
  const [allLocations, setAllLocations] = useState({})
  const [associatedLocations, setAssociatedLocations] = useState([])
  const [errorMessage, setErrorMessage] = useState('')
  const [locationError, setLocationError] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [toAssociate, setToAssociate] = useState([])
  const [toDisassociate, setToDisassociate] = useState([])
  const [unassociatedLocations, setUnassociatedLocations] = useState([])

  const addRemove = () => {
    const toDisassociateProps = toDisassociate.map(location => location.value)
    const postBody = associatedLocations
      .filter(location => !toDisassociateProps.includes(location.value))
      .map(location => location.value)
      .concat(toAssociate.map(location => location.value))
    powerExtensionService
      .postPortalLocations(client, portal, postBody)
      .then(response => {
        setToAssociate([])
        setToDisassociate([])
        handlePortalLocations(response.data)
        setShowSuccessDialog(true)
        setTimeout(() => setShowSuccessDialog(false), 2000)
        setShowDialog(false)
      })
      .catch(e => showError(`The configuration was not saved: ${e}`))
  }

  const handleSubmit = event => {
    event.preventDefault()
    setShowDialog(true)
  }

  const handlePortalLocations = response => {
    setAssociatedLocations(
      Object.keys(response).reduce(
        (result, key) => [...result, responseToOption(key, response[key])],
        [],
      ),
    )
    setUnassociatedLocations(
      Object.keys(allLocations).reduce((result, key) => {
        if (!(key in response)) {
          return [...result, responseToOption(key, allLocations[key])]
        } else {
          return result
        }
      }, []),
    )
    setLocationError(false)
  }

  const responseToOption = (key, item) => {
    return {
      label: `${key} - ${item.prop_name}`,
      value: `${item.prop_id}`,
    }
  }

  const showError = message => {
    setErrorMessage(message)
    setShowErrorDialog(true)
  }

  const updateLocations = async () => {
    powerExtensionService
      .getPortalLocations(client, portal)
      .then(response => handlePortalLocations(response))
      .catch(() => setLocationError(true))
  }

  useEffect(async () => {
    clientsService.getLocationsByClient(client).then(setAllLocations)
  }, [])

  useEffect(async () => {
    await updateLocations()
  }, [allLocations])

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Grid
          container
          data-testid={`${client}-${portal}-locations`}
          key={`row-${portal}-locations-${client}`}
          p={2}
        >
          <Grid item xs={0.5} />
          <Grid item key={`${id}-unassociatedLocations`} xs={5}>
            <AbstractSelector
              error={locationError}
              id={`${id}-unAssociatedLocations-sel`}
              label="Select locations to associate to the portal..."
              options={unassociatedLocations}
              setValue={setToAssociate}
              value={toAssociate}
            />
          </Grid>
          <Grid align="center" item key={`row-${portal}-middle`} xs={1} />
          <Grid item key={`${id}-associatedLocations`} xs={5}>
            <AbstractSelector
              error={locationError}
              id={`${id}-associatedLocations-sel`}
              label="Select locations to disassociate from the portal..."
              options={associatedLocations}
              setValue={setToDisassociate}
              value={toDisassociate}
            />
          </Grid>
          <Grid item xs={0.5} />
          <Grid
            alignItems="center"
            container
            justifyContent="center"
            key={`${id}-save-button`}
            style={{ padding: '5px' }}
          >
            <Button color="primary" type="submit" variant="contained">
							Add/Remove Locations
            </Button>
          </Grid>
        </Grid>
      </form>
      <PowerExtensionDialog
        addRemove={addRemove}
        onClose={() => setShowDialog(false)}
        open={showDialog}
        portal={portal}
      />
      <ErrorDialog
        message={errorMessage}
        onClose={() => setShowErrorDialog(false)}
        open={showErrorDialog}
      />
      <SuccessDialog
        message="The portal had the locations added/removed from it"
        onClose={() => setShowSuccessDialog(false)}
        open={showSuccessDialog}
      />
    </>
  )
}

export default PowerExtensionsLocationSelector
